import { Pencil, RefreshCcw } from 'lucide-react';
import { Avatar } from '../../../../../design-system/avatar/avatar/avatar';
import { Button } from '../../../../../design-system/button/button';
import { Stack } from '../../../../../design-system/stack/stack';
import {
  BeneficiaryDetails,
  formatBeneficiaryDescription,
  getBeneficiaryAccountDetails,
} from '../utils/beneficiary-utils';

type VendorInformationProps = {
  vendorLogo: string;
  vendorName: string;
  beneficiary: BeneficiaryDetails | null;
  vendorDescription?: string | null;
  onEditVendor: () => void;
  onChangeVendor: () => void;
};

export const VendorInformation = ({
  beneficiary,
  vendorLogo,
  vendorName,
  vendorDescription,
  onEditVendor,
  onChangeVendor,
}: VendorInformationProps) => {
  const isBeneficiary = !!beneficiary?.id;

  const accountDetails = beneficiary ? getBeneficiaryAccountDetails(beneficiary) : null;

  // TODO: remove the non-null assertion operator !
  const displayName = isBeneficiary ? beneficiary!.name : vendorName;
  const displayDescription =
    isBeneficiary && accountDetails
      ? formatBeneficiaryDescription({ beneficiary: beneficiary!, accountDetailsConfig: accountDetails })
      : (vendorDescription ?? '');

  return (
    <div className="w-full">
      <VendorCard
        imageUrl={vendorLogo}
        displayName={displayName}
        description={displayDescription}
        onEditVendor={onEditVendor}
        onChangeVendor={onChangeVendor}
        isBeneficiary={isBeneficiary}
      />
    </div>
  );
};

type VendorCardProps = {
  imageUrl: string;
  displayName: string;
  description: string;
  isBeneficiary: boolean;
  onChangeVendor: () => void;
  onEditVendor: () => void;
};

const VendorCard = ({
  imageUrl,
  displayName,
  description,
  isBeneficiary,
  onChangeVendor,
  onEditVendor,
}: VendorCardProps) => (
  <div className="w-full rounded-lg border-[0.5px] border-border p-4 text-wrap">
    <div className="flex w-full">
      <Avatar avatarUrl={imageUrl} name={displayName} size="sm" className="min-w-8" />
      <Stack space={1} className="ml-3 w-full">
        <div className="text-sm font-normal">{displayName}</div>
        <div className="text-left text-xs font-normal break-words text-muted-foreground">{description}</div>
      </Stack>
      {!isBeneficiary && (
        <div className="ml-2 flex items-center gap-3">
          <Button variant="ghost" onClick={onEditVendor} className="p-0" aria-label="Edit vendor">
            <Pencil className="text-muted-foreground" size={16} />
          </Button>
          <Button variant="ghost" onClick={onChangeVendor} className="p-0" aria-label="Change vendor">
            <RefreshCcw className="text-muted-foreground" size={16} />
          </Button>
        </div>
      )}
    </div>
  </div>
);
